<template>
  <div
    class="wrapper"
    :class="{
      'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      listening: listening && currentConfig.sttSubtitles
    }"
  >
    <div
      v-if="subtitle"
      ref="subtitleRef"
      class="subtitle"
    >
      <div class="text">
        {{ subtitle }}
      </div>
      <div
        :style="{
          transform: `translateY(${subtitleAnimation.animationValue})`,
          transition: subtitleAnimation.current === -1 ? 'none' : null,
        }"
        class="text-animated"
      >
        <div ref="subtitleTextRef">
          {{ subtitle }}
        </div>
      </div>
    </div>
    <div
      v-else-if="listening && currentConfig.sttSubtitles"
      :class="{
        'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      }"
      class="listening"
    >
      <div class="icon-container">
        <div
          v-if="!recognizedText.text"
          class="pulse-ring"
        />
        <img :src="`icons/${!recognizedText.text ? 'mic' : 'tts-animation'}.svg`">
      </div>
      <div
        v-if="recognizedText.text"
        class="recognized-text"
      >
        {{ recognizedText.text }}
      </div>
    </div>
  </div>
</template>

<script>
import { useStt } from '@ht/lib-plural-vue';
import { ref, watch, defineComponent, nextTick } from 'vue';
import useConfig from '@/composables/useConfig';
import useContent from '@/composables/useContent';

export default defineComponent({
  name: 'SubtitlesComponent',

  setup() {
    const { subtitle, isHideAvatar, isKeepAvatarDesign } = useContent();
    const { sttRunning, recognizedText } = useStt();
    const { currentConfig } = useConfig();

    const listening = ref(false);
    let listeningTimeout = null;

    watch(sttRunning, (isRunning) => {
      if (isRunning) {
        // we need to avoid showing listen icon in case there is a robot speech
        listeningTimeout = setTimeout(() => {
          listening.value = true;
        }, 500);
      } else {
        clearTimeout(listeningTimeout);
        listening.value = false;
      }
    });

    const subtitleTextRef = ref(null);
    const subtitleRef = ref(null);

    const subtitleAnimation = ref({
      current: 0,
      animationValue: 0,
      max: 0,
      timer: null,
    });

    function animationStep() {
      subtitleAnimation.value.current += 1;
      const percentLineHeight = 100 / subtitleAnimation.value.max;
      subtitleAnimation.value.animationValue = `${((percentLineHeight * (subtitleAnimation.value.max - 1)) / 2 - percentLineHeight * subtitleAnimation.value.current)}%`;

      if (subtitleAnimation.value.current < subtitleAnimation.value.max - 1) {
        subtitleAnimation.value.timer = setTimeout(animationStep, 3000);
      }
    }

    function calculateSubtitleContent() {
      if (subtitleAnimation.value.timer) {
        clearTimeout(subtitleAnimation.value.timer);
      }

      const lineHeight = parseFloat(getComputedStyle(subtitleTextRef.value).lineHeight);
      const textHeight = subtitleTextRef.value.clientHeight;
      const numberOfLines = Math.round(textHeight / lineHeight);

      subtitleAnimation.value.current = -1;
      subtitleAnimation.value.animationValue = 0;
      subtitleAnimation.value.max = numberOfLines;

      const percentLineHeight = 100 / subtitleAnimation.value.max;
      subtitleAnimation.value.animationValue = `${((percentLineHeight * (subtitleAnimation.value.max + 1)) / 2)}%`;

      setTimeout(animationStep, 100);
    }

    watch([subtitle, subtitleTextRef], () => {
      if (subtitleTextRef.value) {
        if (subtitle.value && subtitle.value.trim()) {
          nextTick(calculateSubtitleContent);
        } else {
          clearTimeout(subtitleAnimation.value.timer);
        }
      }
    });

    return {
      subtitle,
      listening,
      recognizedText,
      currentConfig,
      isHideAvatar,
      isKeepAvatarDesign,
      subtitleTextRef,
      subtitleRef,
      subtitleAnimation,
    };
  },
});
</script>

<style lang="scss" scoped>
.wrapper {
  position: absolute;
  top: 60px;
  left: 0;
  z-index: 10000;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 16px;

  @media screen and (min-width: $desktop_s_breakpoint - 1) {
    top: 65px;
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    padding: 0 40px;
    top: 188px;
  }

  &.listening {
    display: block;
  }

  .subtitle {
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    pointer-events: none;
    padding: 4px 5px;
    opacity: 0.8;
    background-color: black;
    border-radius: 8px;
    height: 36px;
    max-height: 28px;
    overflow: hidden;
    margin: 0 auto;

    .text {
      display: none;
    }

    .text-animated {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.7em;
      transition: transform 0.2s ease-in-out;
    }

    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      height: 40px;
      max-height: 40px;
      padding: 4px 12px;
      min-width: 100px;
      width: fit-content;

      .text-animated {
        display: block;
        font-size: 24px;
      }
      .text {
        display: none;
      }
    }

    // for big Full HD screen
    @media screen and (orientation: portrait) and (min-width: $desktop_s_breakpoint - 1px) {
      height: 40px;
      max-height: 40px;
      padding: 4px 12px;

      .text {
        display: none;
      }
      .text-animated {
        display: block;
        font-size: 24px;
      }
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      height: 80px;
      max-height: 80px;
      padding: 8px 24px;
      border-radius: 16px;

      .text {
        display: none;
      }
      .text-animated {
        display: block;
        font-size: 48px;
        line-height: 64px;
      }
    }

    @media screen and (orientation: landscape) and (min-width: $tv_screen_breakpoint) {
      height: 80px;
      max-height: 80px;
      padding: 8px 24px;
      border-radius: 16px;

      .text-animated {
        display: block;
        font-size: 48px;
        line-height: 64px;
      }
      .text {
        display: none;
      }
    }
  }

  .listening {
    position: initial;
    display: flex;
    justify-content: left;
    align-items: center;
    flex-direction: row;
    background: white;
    border-radius: 14px;
    box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
    width: fit-content;
    height: 28px;
    max-height: 28px;
    min-width: 28px;
    max-width: 100%;
    margin-left: 5px;

    .icon-container {
      width: 28px;
      height: 28px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      .pulse-ring {
        content: '';
        width: 120%;
        height: 120%;
        background: white;
        border-radius: 50%;
        position: absolute;
        animation: pulsate infinite 2s;
      }
      @keyframes pulsate {
        0% {
          -webkit-transform: scale(1, 1);
          opacity: 1;
        }
        100% {
          -webkit-transform: scale(1.5, 1.5);
          opacity: 0;
        }
      }

      img {
        width: 20px;
        height: 20px;
        margin: 3px;
        z-index: 1;
      }
    }

    .recognized-text {
      font-size: 14px;
      // fix for 1 line text ellipsis
      // https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: left;
      margin: 0 4px;
    }

    @media screen and (min-width: $tablet_breakpoint) {
      height: 40px;
      max-height: 40px;
      min-width: 40px;
      margin-left: 4px;
      border-radius: 20px;

      .icon-container {
        width: 40px;
        height: 40px;

        img {
          width: 24px;
          height: 24px;
          margin: 4px;
        }
      }
    }

    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      .icon-container {
        width: 40px;
        height: 40px;

        img {
          width: 24px;
          height: 24px;
          margin: 4px;
        }
      }

      .recognized-text {
        font-size: 24px;
        margin: 0 8px;
      }
    }

    // for big Full HD screen
    @media screen and (orientation: portrait) and (min-width: $desktop_s_breakpoint - 1px) {
      .icon-container {
        width: 40px;
        height: 40px;

        img {
          width: 24px;
          height: 24px;
          margin: 4px;
        }
      }

      .recognized-text {
        font-size: 24px;
        margin: 0 8px;
      }
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      height: 116px;
      max-height: 116px;
      min-width: 116px;
      border-radius: 58px;
      margin-left: 12px;

      .icon-container {
        width: 116px;
        height: 116px;

        img {
          width: 70px;
          height: 70px;
          margin: 6px;
        }
      }

      .recognized-text {
        font-size: 48px;
        margin: 0 16px;
      }
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      height: 28px;
      max-height: 28px;
      min-width: 28px;
      border-radius: 14px;
      margin-left: 5px;

      .icon-container {
        width: 28px;
        height: 28px;

        img {
          width: 20px;
          height: 20px;
          margin: 3px;
        }
      }

      .recognized-text {
        font-size: 14px;
        margin: 0 4px;
      }
    }
  }

  &.has-avatar {
    @media screen and (orientation: landscape) and (max-width: $desktop_s_breakpoint - 1px) {
      top: initial;
      bottom: 20px;
      justify-content: center;
      width: 38.5%;
      height: initial;

      .subtitle {
        padding: 5px;
        max-height: initial;
        height: auto;

        .text {
          display: block;
          font-size: 14px;
          line-height: 18px;
        }

        .text-animated {
          display: none;
        }
      }

      .listening {
        max-width: 100%;

        .icon-container {
          width: 28px;
          height: 28px;

          img {
            width: 20px;
            height: 20px;
          }
        }
      }
    }

    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      top: initial;
      bottom: 20px;
      justify-content: center;
      width: 41%;
      height: initial;

      .subtitle {
        padding: 10px;
        max-height: initial;
        height: auto;

        .text {
          display: block;
          font-size: 24px;
          line-height: 32px;
        }

        .text-animated {
          display: none;
        }
      }
    }

    @media screen and (orientation: landscape) and (min-width: $tv_screen_breakpoint) {
      .subtitle {
        padding: 8px 24px;
        border-radius: 16px;

        .text-animated {
          display: none;
        }
        .text {
          display: block;
          font-size: 48px;
          line-height: 64px;
        }
      }
    }
  }
}
</style>
